import type { ReactNode } from 'react';

import { cn } from '@rocco/utils/cn';
import { LookbookCoverPreview } from './LookbookCoverPreview';

/**
 * How much of the picture's width the book takes, and how far it is lifted
 * off it. Shared so the book stands on its picture the same way wherever the
 * pair is drawn — the page's frame, and the gate's success screen.
 */
const COVER_ON_FRAME = cn(
    'absolute top-1/2 left-1/2 w-[48%] -translate-x-1/2 -translate-y-1/2',
    '[box-shadow:0_12px_40px_0_rgba(0,0,0,0.35)]',
);

export interface LookbookFrameProps {
    /**
     * The project's own picture, the ground the book stands on. Supplied as a
     * node rather than a src: the page wants Rocco's responsive `Image` and
     * the gate's modal wants a plain tag, and the frame does not care which.
     * It should fill the square and carry its own `grayscale`.
     */
    picture: ReactNode;
    /** The book: the three strings its cover is set from. */
    cover: {
        title: string;
        professional: string;
        imageSrc?: string;
    };
    className?: string;
    /** Added to the cover — the page's hover lift lives here. */
    coverClassName?: string;
    /** Anything riding on the frame, such as the page's download pill. */
    children?: ReactNode;
}

/**
 * The lookbook on the project's own picture: a square of the photography,
 * desaturated so the book is the colour in it, with the cover standing
 * centred and lifted off it.
 *
 * The composition is the page's, and the gate borrows it to hand the book
 * back looking like the thing the reader clicked rather than a file name.
 */
export const LookbookFrame = ({
    picture,
    cover,
    className,
    coverClassName,
    children,
}: LookbookFrameProps) => (
    <div
        className={cn(
            'relative aspect-square overflow-hidden bg-white/5',
            className,
        )}
    >
        {picture}
        <LookbookCoverPreview
            {...cover}
            className={cn(COVER_ON_FRAME, coverClassName)}
        />
        {children}
    </div>
);
